/**
 * Auth Agent: Handles login, registration, token storage, and current-user resolution.
 */

import { useState, useEffect, useCallback } from 'react';
import APIAgent from './apiAgent';

const USER_KEY = 'cutthe6.com_auth_user';

// ── Token helpers ──────────────────────────────────────────────────────────

export function getToken() {
  // Token is now stored in an httpOnly cookie — not accessible from JS.
  // This function is kept for backward compatibility but always returns null.
  return null;
}

function saveSession(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  if (user?.id != null) {
    localStorage.setItem('cutthe6.com_user_id', String(user.id));
  }
}

export function clearSession() {
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem('cutthe6.com_user_id');
}

// ── Auth API calls ─────────────────────────────────────────────────────────

/**
 * Register a new user account.
 * @param {{ first_name, last_name, email, password, role?, phone? }} data
 * @returns {{ access_token, token_type, user }}
 */
export async function register(data) {
  const result = await APIAgent.request('/api/auth/register', 'POST', data);
  saveSession(result.user);
  return result;
}

/**
 * Log in with email + password.
 * @param {{ email, password }} data
 * @returns {{ access_token, token_type, user }}
 */
export async function login(data) {
  const result = await APIAgent.request('/api/auth/login', 'POST', data);
  saveSession(result.user);
  return result;
}

/**
 * Validate the stored token by calling /api/auth/me.
 * Clears storage and returns null if the token is invalid.
 * @returns {object|null} User object or null.
 */
export async function validateToken() {
  try {
    const user = await APIAgent.request('/api/auth/me', 'GET', null, {}, { skipAuthRedirect: true, timeout: 5000 });
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    localStorage.setItem('cutthe6.com_user_id', String(user.id));
    return user;
  } catch {
    clearSession();
    return null;
  }
}

/**
 * Request a password reset email.
 * @param {string} email
 * @returns {{ message: string }}
 */
export async function forgotPassword(email) {
  return APIAgent.request('/api/auth/forgot-password', 'POST', { email });
}

/**
 * Reset password using a token received by email.
 * @param {string} token
 * @param {string} newPassword
 * @returns {{ message: string }}
 */
export async function resetPassword(token, newPassword) {
  return APIAgent.request('/api/auth/reset-password', 'POST', { token, new_password: newPassword });
}

// ── useAuth hook ──────────────────────────────────────────────────────────

/**
 * React hook that exposes the current authenticated user and auth helpers.
 *
 * @returns {{
 *   user: object|null,
 *   loading: boolean,
 *   error: string|null,
 *   login: (email, password) => Promise<void>,
 *   register: (data) => Promise<void>,
 *   logout: () => void,
 * }}
 */
export function useAuth() {
  // Do NOT seed state from localStorage here. The cached user is only a UI
  // hint from a previous session; the source of truth is the httpOnly cookie
  // validated by /api/auth/me on mount. Seeding from cache caused a "login
  // loop" symptom: after logout the prior user briefly re-rendered before the
  // /me call rejected the cleared cookie.
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [devBypass, setDevBypass] = useState(false);

  // On mount: validate stored token
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const bypassEnabled = await checkDevBypass();
      if (cancelled) return;
      if (bypassEnabled) setDevBypass(true);

      // Always validate the token server-side, even in dev bypass mode.
      // This prevents stale localStorage data from triggering authenticated
      // renders with an expired cookie, which causes 401 loops.
      const validUser = await validateToken();
      if (!cancelled) {
        setUser(validUser);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleLogin = useCallback(async (email, password) => {
    setError(null);
    try {
      const result = await login({ email, password });
      setUser(result.user);
    } catch (err) {
      setError(err.message || 'Login failed');
      throw err;
    }
  }, []);

  const handleRegister = useCallback(async (data) => {
    setError(null);
    try {
      const result = await register(data);
      setUser(result.user);
    } catch (err) {
      setError(err.message || 'Registration failed');
      throw err;
    }
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await APIAgent.request('/api/auth/logout', 'POST');
    } catch {
      // Cookie may already be expired — clear local state regardless
    }
    clearSession();
    setUser(null);
  }, []);

  const switchUser = useCallback(async (userId) => {
    setError(null);
    try {
      const result = await APIAgent.request(`/api/auth/dev/switch/${userId}`, 'POST');
      saveSession(result.user);
      setUser(result.user);
    } catch (err) {
      setError(err.message || 'Switch failed');
      throw err;
    }
  }, []);

  const adminSwitchUser = useCallback(async (userId) => {
    setError(null);
    try {
      const result = await APIAgent.request(`/api/admin/user-switch/${userId}`, 'POST');
      saveSession(result.user);
      setUser(result.user);
    } catch (err) {
      setError(err.message || 'Switch failed');
      throw err;
    }
  }, []);

  return {
    user,
    loading,
    error,
    devBypass,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    switchUser,
    adminSwitchUser,
  };
}

// ── Dev bypass helpers ─────────────────────────────────────────────────────

export async function checkDevBypass() {
  try {
    const result = await APIAgent.request('/api/auth/dev/status', 'GET');
    return result.bypass_enabled === true;
  } catch {
    return false;
  }
}

export async function fetchDevUsers() {
  return APIAgent.request('/api/auth/dev/users', 'GET');
}
